import { HttpClient, HttpResponse } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface RoomObject {
  id: number;
  name: string;
  address: {
    id: number;
    address1: string;
    address2?: string;
    address_name?: string;
    color?: string;
  };
  installation_date?: string;
  consumable: boolean;
  model?: {
    id: number;
    name: string;
    model: string;
    category?: {
      id: number;
      name: string;
      default_icon_url?: string;
    };
  };
  [key: string]: any;
}

export interface RoomObjectsResponse {
  body: RoomObject[];
  totalRecords: number;
}

export interface GetRoomObjectsParams {
  page?: number;
  perPage?: number;
  consumable?: boolean;
  search?: string;
}

export interface CreateRoomObjectData {
  category_id: number;
  address_id: number;
  installation_date?: string;
  model_id?: number;
  photo_url?: string;
  min_quantity?: number;
  max_quantity?: number;
  current_quantity?: number;
  [key: string]: any;
}

@Injectable({ providedIn: 'root' })
export class AssetsApiService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = environment.api_url;

  getRoomObjects(params: GetRoomObjectsParams = {}): Observable<RoomObjectsResponse> {
    let url = `${this.baseUrl}api/v1/customer/room-objects?includes=with_address_details`;

    if (params.page) {
      url += `&page=${params.page}`;
    }
    if (params.perPage) {
      url += `&per_page=${params.perPage}`;
    }
    if (params.consumable !== undefined) {
      url += `&filters[consumable]=${params.consumable}`;
    }
    if (params.search) {
      url += `&filters[name_search]=${params.search}`;
    }

    return this.http.get<RoomObject[]>(url, { observe: 'response' }).pipe(
      map((response: HttpResponse<RoomObject[]>) => ({
        body: response.body || [],
        totalRecords: Number(response.headers.get('X-Total-Records')) || 0,
      }))
    );
  }

  getRoomObject(id: number): Observable<RoomObject> {
    const url = `${this.baseUrl}api/v1/customer/room-objects/${id}?includes=with_address_tasks,with_change_history,with_address_details,characteristics,with_address_task_lists,address_task_groups`;
    return this.http.get<RoomObject>(url);
  }

  createRoomObject(data: CreateRoomObjectData): Observable<RoomObject> {
    const url = `${this.baseUrl}api/v1/customer/room-objects/create-many`;
    return this.http.post<RoomObject>(url, data);
  }

  updateRoomObject(id: number, data: Partial<RoomObject>): Observable<RoomObject> {
    const url = `${this.baseUrl}api/v1/customer/room-objects/${id}`;
    return this.http.put<RoomObject>(url, data);
  }

  inactivateRoomObject(id: number): Observable<void> {
    const url = `${this.baseUrl}api/v1/customer/room-objects/${id}/inactivate`;
    return this.http.post<void>(url, {});
  }
}
